import PageContainer from '../../components/layout/PageContainer'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import DocAnalysis from './DocAnalysis'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import Button from '@mui/material/Button'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import EmptyState from '../../components/EmptyState'
import { DashboardDetailResponse } from '@counsel-project/counsel-audit-ai-api'
import { AlertsGrid } from './AlertsGrid'
import { useAuditHistory } from '../../queries/hooks/useAuditHistory'
import { useState, useCallback, useMemo, useEffect } from 'react'
import usePageTitle from '../../util/usePageTitle'
import { MenuItem, Paper, Select, FormControl, InputLabel } from '@mui/material'
import { formatDate } from '../dashboards/charts/_helpers'
import { DateOfServiceCell } from '../dashboards/dashboardListUi'
import { getErrorMessage } from '../../util/errorHandling'
import EmptyPageContainer from '../../components/EmptyPageContainer'

const AlertPage = () => {
  usePageTitle('Alerts')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const auditId = searchParams.get('auditId')

  const { data: auditHistory, isLoading: isDocumentLoading, error } = useAuditHistory(auditId)

  const [selectedVersion, setSelectedVersion] = useState(0)

  useEffect(() => {
    setSelectedVersion(0)
  }, [auditId])

  useEffect(() => {
    if (auditHistory) {
      setSelectedVersion(auditHistory.findIndex((doc) => doc?._id === auditId) ?? 0)
    }
  }, [auditHistory, auditId])

  const cleanContent = useCallback((content: string): string => {
    return content
      .split('\n')
      .filter((line) => {
        const trimmed = line.trim()
        return (
          !trimmed.startsWith('[//]') &&
          !trimmed.startsWith('[//]:') &&
          !(trimmed.startsWith('[') && trimmed.endsWith(']'))
        )
      })
      .map((line) => line.replace(/\[.*?\]|\\(.)/g, (match, escaped) => escaped ?? ''))
      .join('\n')
  }, [])

  const handleNavBack = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      navigate(-1)
    },
    [navigate]
  )

  const { cleanedMarkdown, cleanedAlerts } = useMemo(() => {
    if (!auditHistory?.[selectedVersion]) {
      return { cleanedMarkdown: '', cleanedAlerts: [] }
    }

    const cleanedMarkdown = cleanContent(auditHistory[selectedVersion]?.document.content ?? '')
    const cleanedAlerts: DashboardDetailResponse['result']['alerts'] =
      auditHistory[selectedVersion]?.alerts.map((alert) => ({
        ...alert,
        text: cleanContent(alert.text),
      })) ?? []

    return { cleanedMarkdown, cleanedAlerts }
  }, [auditHistory, selectedVersion, cleanContent])

  if (!auditId) return <AlertsGrid />

  if (isDocumentLoading) {
    return (
      <PageContainer>
        <Button
          component={Link}
          to="/alerts"
          startIcon={<ArrowBackIcon />}
          onClick={handleNavBack}
          data-qa-id="document-back-button"
        >
          Back to Documents
        </Button>
        <EmptyPageContainer>
          <EmptyState
            emptyStateVariant="loading"
            title="Loading the document"
            description="Please wait while we load the document"
          />
        </EmptyPageContainer>
      </PageContainer>
    )
  }

  if (error || !auditHistory) {
    return (
      <PageContainer>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              component={Link}
              to="/alerts"
              startIcon={<ArrowBackIcon />}
              onClick={handleNavBack}
              data-qa-id="document-back-button"
            >
              Back to Documents
            </Button>
          </Grid>
          <Grid item xs={12}>
            <EmptyState
              emptyStateVariant="error"
              sx={{ height: '300px' }}
              title="Selected document not found"
              description={getErrorMessage(error, 'The document you selected was not found')}
            />
          </Grid>
        </Grid>
      </PageContainer>
    )
  }

  if (auditHistory?.[selectedVersion]) {
    const currentAudit = auditHistory[selectedVersion]

    // This should never happen, going around for typescript
    if (!currentAudit) {
      return null
    }

    return (
      <PageContainer>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Button
                component={Link}
                to="/alerts"
                startIcon={<ArrowBackIcon />}
                onClick={handleNavBack}
                data-qa-id="document-back-button"
              >
                Back to Documents
              </Button>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="audit-version-label">Select Audit Version</InputLabel>
                <Select
                  labelId="audit-version-label"
                  data-qa-id="document-audit-version-select"
                  value={selectedVersion}
                  onChange={(e) => setSelectedVersion(e.target.value as number)}
                  label="Select Audit Version"
                  sx={{
                    backgroundColor: 'white',
                  }}
                >
                  {auditHistory.map((doc, index) => (
                    <MenuItem key={index} value={index}>
                      {formatDate(doc?.createdAt ?? '')} {index === 0 ? '(Newest)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" textAlign="center">
              {currentAudit.document.title}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Grid container spacing={3}>
                {/* Clinician Information */}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                    Clinician
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {currentAudit.user.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentAudit.user.email}
                  </Typography>
                </Grid>

                {/* Patient Information */}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                    Patient
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {currentAudit.patient.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    MRN: {currentAudit.patient.mrn}
                  </Typography>
                </Grid>

                {/* Audit Information */}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                    Audit Details
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Date:</strong> {formatDate(currentAudit.createdAt ?? '')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }} data-qa-id="document-date-of-service">
                    <strong>Date of Service:</strong>{' '}
                    <DateOfServiceCell visitDate={currentAudit.document.visitDate} />
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Status:</strong>{' '}
                    <Box
                      component="span"
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        backgroundColor: currentAudit.remediated
                          ? 'warning.light'
                          : 'success.light',
                        color: currentAudit.remediated
                          ? 'warning.contrastText'
                          : 'success.contrastText',
                      }}
                    >
                      {currentAudit.remediated ? 'Old Version' : 'Newest Version'}
                    </Box>
                  </Typography>
                  {currentAudit.program && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Program:</strong> {currentAudit.program}
                    </Typography>
                  )}
                  {currentAudit.location && (
                    <Typography variant="body2">
                      <strong>Location:</strong> {currentAudit.location}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <DocAnalysis markdown={cleanedMarkdown} alerts={cleanedAlerts} />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Button
                onClick={() =>
                  setSelectedVersion(Math.min(selectedVersion + 1, auditHistory.length - 1))
                }
                disabled={selectedVersion === auditHistory.length - 1}
                data-qa-id="document-older-version-button"
              >
                <ArrowBackIcon /> Older version
              </Button>
              <Button
                onClick={() => setSelectedVersion(Math.max(selectedVersion - 1, 0))}
                disabled={selectedVersion === 0}
                data-qa-id="document-newer-version-button"
              >
                Newer version <ArrowForwardIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </PageContainer>
    )
  }

  return <AlertsGrid />
}

export default AlertPage
